import React, { useState } from 'react';
import { motion } from 'framer-motion';

/**
 * Video welcome screen rendered as the first view of the /funnel-auth flow.
 */

const containerVariants = {
  enter: {},
  center: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
};

const itemVariants = {
  enter: { opacity: 0, y: 18 },
  center: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const DRAWING_ANIMATION_SRC = '/assets/pixley-top-animation.webp';
const DRAWING_FALLBACK_SRC = '/assets/pixley-top-animation-fallback.png';

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m8.5 4.5 7.5 7.5-7.5 7.5" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FunnelWelcomePage = ({ onContinue }) => {
  const [drawingSrc, setDrawingSrc] = useState(DRAWING_ANIMATION_SRC);

  return (
    <main className="fw-bg">
      <FunnelWelcomeStyles />
      <div className="fw-frame">
        <video
          className="fw-video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        >
          <source src="/assets/landingpage-mobile.mp4" type="video/mp4" />
        </video>
        <div className="fw-video-vignette" aria-hidden="true" />
        <img
          className="fw-drawing"
          src={drawingSrc}
          width="556"
          height="418"
          alt=""
          aria-hidden="true"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          onError={() => {
            if (drawingSrc !== DRAWING_FALLBACK_SRC) {
              setDrawingSrc(DRAWING_FALLBACK_SRC);
            }
          }}
        />

        <motion.section
          className="fw-panel"
          variants={containerVariants}
          initial="enter"
          animate="center"
          aria-labelledby="fw-title"
        >
          <motion.h1 id="fw-title" className="fw-title" variants={itemVariants}>
            <span>Stories Made For</span>
            <span>Your Child</span>
          </motion.h1>

          <motion.p className="fw-subtitle" variants={itemVariants}>
            Pixley creates magical,<br />
            <strong>personalized</strong> cartoons<br />
            your child will love.
          </motion.p>

          <motion.button
            type="button"
            className="fw-cta"
            onClick={onContinue}
            variants={itemVariants}
            whileTap={{ scale: 0.985 }}
          >
            <span>Continue</span>
            <span className="fw-cta-arrow"><ArrowIcon /></span>
          </motion.button>

        </motion.section>
      </div>
    </main>
  );
};

const FunnelWelcomeStyles = () => (
  <style>{`
    .fw-bg {
      min-height: 100vh;
      min-height: 100dvh;
      background:
        radial-gradient(1000px 700px at 80% 0%, rgba(124,92,255,0.1), transparent 60%),
        linear-gradient(180deg, #F5FAFF 0%, #E8F1FF 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: 'Fredoka', 'Inter', sans-serif;
    }

    /* container-type makes this the reference box for the cqw units used by the
       type below. Everything in here is laid out for a phone, but on desktop the
       frame is a ~400px mockup inside a ~1440px window — so vw units (which
       always measure the WINDOW) blew the type up to its clamp maximum, wrapped
       the title onto a third line and pushed the CTA out through overflow:hidden.
       cqw measures this frame instead, so the mockup renders as a faithful
       scaled copy of the phone layout at any window size.
       Only inline-size containment: the frame's width is set explicitly in both
       branches and every child is absolutely positioned, so nothing here sizes
       itself from content. */
    .fw-frame {
      position: relative;
      container-type: inline-size;
      width: 100%;
      height: 100vh;
      height: 100dvh;
      min-height: 620px;
      overflow: hidden;
      background: #9BCDF8;
      isolation: isolate;
    }

    @media (min-aspect-ratio: 3/4) {
      .fw-frame {
        width: auto;
        height: min(calc(100dvh - 48px), 920px);
        min-height: min(calc(100dvh - 48px), 620px);
        /* Matches the 390x844 viewport this layout is designed against, rather
           than a squat 9/16. At 9/16 the frame came out 479px wide but no taller
           than a phone, so the panel's contents could never fit the height no
           matter how they were scaled. */
        aspect-ratio: 390 / 844;
        border-radius: 32px;
        box-shadow: 0 24px 60px rgba(11,26,51,0.18), 0 4px 14px rgba(11,26,51,0.08);
      }
    }

    .fw-video {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center center;
      transform: scale(1.1) translateY(-1%);
      transform-origin: center center;
      will-change: transform;
    }

    .fw-video-vignette {
      position: absolute;
      inset: 34% 0 0;
      pointer-events: none;
      background: linear-gradient(
        180deg,
        transparent 0%,
        rgba(4, 24, 54, 0.08) 22%,
        rgba(4, 24, 54, 0.34) 62%,
        rgba(4, 24, 54, 0.62) 100%
      );
    }

    .fw-drawing {
      position: absolute;
      z-index: 1;
      top: clamp(-22px, calc(-8vh + 70px), 22px);
      left: 50%;
      width: min(85.7%, 459px);
      height: auto;
      transform: translate3d(-50%, 0, 0);
      background: transparent;
      pointer-events: none;
      will-change: transform;
    }

    .fw-panel {
      position: absolute;
      z-index: 2;
      left: -3%;
      right: -3%;
      bottom: -15px;
      /* 53%, matching the override below. At the old 78% the panel started high
         enough to run the title into the PIXLEY logo — visible only in the one
         region neither media query covers (taller than 960px AND narrower than
         4:3, e.g. a 600x1000 browser window), since every phone and desktop size
         picks up the 53% from the override anyway. */
      height: 53%;
      min-height: 430px;
      max-height: 900px;
      box-sizing: border-box;
      padding:
        clamp(16px, 2.5vh, 28px)
        calc(8% + env(safe-area-inset-right, 0px))
        calc(66px + env(safe-area-inset-bottom, 0px))
        calc(8% + env(safe-area-inset-left, 0px));
      background: transparent;
      color: #FFF;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    .fw-title {
      position: relative;
      margin: 0;
      /* vw line is the fallback; the cqw line wins wherever container queries
         are supported. Same pairing on every clamp below. */
      font-size: clamp(49px, 11vw, 58px);
      font-size: clamp(30px, 11cqw, 58px);
      font-weight: 700;
      line-height: 1.04;
      letter-spacing: -0.035em;
    }
    .fw-title > span { display: block; }
    .fw-title > span:first-child {
      color: #FFF;
      text-shadow: 0 3px 16px rgba(3, 22, 50, 0.72);
    }
    .fw-title > span:nth-child(2) {
      color: #63B2FF;
      margin-top: 5px;
      text-shadow: 0 3px 16px rgba(3, 22, 50, 0.76);
    }

    .fw-subtitle {
      margin: calc(clamp(13px, 1.7vh, 18px) + 10px) 0 0;
      color: rgba(255,255,255,0.94);
      font-family: 'Inter', 'Fredoka', sans-serif;
      font-size: clamp(27px, 7.1vw, 37px);
      font-size: clamp(17px, 7.1cqw, 37px);
      font-weight: 500;
      line-height: 1.38;
      letter-spacing: -0.012em;
      text-shadow: 0 2px 10px rgba(3,22,50,0.75);
    }
    .fw-subtitle strong { color: #76BCFF; font-weight: 800; }

    .fw-benefits {
      width: 100%;
      max-width: 440px;
      display: grid;
      grid-template-columns: 1fr 1px 1fr 1px 1fr;
      align-items: center;
      margin: clamp(20px, 2vh, 23px) 0 0;
    }
    .fw-divider {
      width: 1px;
      height: 70px;
      background: rgba(255,255,255,0.38);
    }
    .fw-benefit {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
    }
    .fw-benefit-icon {
      width: clamp(64px, 15.5vw, 80px);
      height: clamp(64px, 15.5vw, 80px);
      display: grid;
      place-items: center;
      border-radius: 19px;
      background: rgba(219,237,255,0.9);
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.72), 0 6px 16px rgba(3,22,50,0.24);
    }
    .fw-benefit-love .fw-benefit-icon { background: linear-gradient(145deg, #FFF0F5, #FFDCE8); }
    .fw-benefit-safe .fw-benefit-icon { background: linear-gradient(145deg, #FFF9DB, #FFEAA1); }
    .fw-benefit-secure .fw-benefit-icon { background: linear-gradient(145deg, #EAF5FF, #CFE7FF); }
    .fw-benefit-icon svg {
      width: 72%;
      height: 72%;
      overflow: visible;
      transform: none;
      filter: drop-shadow(0 2px 1px rgba(3,22,50,0.12));
    }
    .fw-benefit-label {
      color: #FFF;
      font-size: clamp(15px, 3.8vw, 18px);
      font-weight: 700;
      line-height: 1;
      text-shadow: 0 2px 8px rgba(3,22,50,0.82);
    }

    .fw-cta {
      position: relative;
      top: 5px;
      width: min(100%, 448px);
      min-height: clamp(64px, 8vh, 90px);
      margin-top: 28px;
      padding: 14px 64px 14px 32px;
      border: none;
      border-radius: 999px;
      background: #247CFF;
      color: #FFF;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: clamp(30px, 7vw, 40px);
      font-size: clamp(30px, 7cqw, 40px);
      font-weight: 700;
      line-height: 1;
      cursor: pointer;
      box-shadow: 0 13px 30px rgba(13,91,226,0.42);
      transition: transform 180ms ease, box-shadow 180ms ease, filter 180ms ease;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    }
    .fw-cta:hover {
      box-shadow: 0 16px 34px rgba(13,91,226,0.48);
    }
    .fw-cta:focus-visible { outline: 4px solid rgba(36,124,255,0.3); outline-offset: 4px; }
    .fw-cta-arrow {
      position: absolute;
      top: 50%;
      right: clamp(23px, 5.6vw, 45px);
      right: clamp(23px, 5.6cqw, 45px);
      display: grid;
      place-items: center;
      width: clamp(28px, 6.5vw, 37px);
      width: clamp(28px, 6.5cqw, 37px);
      height: clamp(28px, 6.5vw, 37px);
      height: clamp(28px, 6.5cqw, 37px);
      transform: translateY(-50%);
    }
    .fw-cta-arrow svg { width: 100%; height: 100%; }
    @media (max-height: 960px), (min-aspect-ratio: 3/4) {
      .fw-panel {
        height: 53%;
        padding-top: 35px;
        padding-bottom: calc(60px + env(safe-area-inset-bottom, 0px));
      }
      .fw-title { font-size: clamp(48px, 12.5vw, 62px); }
      .fw-subtitle { margin-top: 18px; font-size: clamp(25px, 6.5vw, 33px); line-height: 1.28; }
      .fw-cta { min-height: 60px; margin-top: 24px; font-size: 28px; }
      .fw-title > span:nth-child(2) { margin-top: 2px; }

      /* Container-relative versions. The floors are much lower than the vw
         fallbacks above on purpose: those high minimums existed to stop
         viewport-based type collapsing on a wide desktop, but now that the type
         measures the frame it has to be free to shrink WITH the frame — on a
         short window the mockup is only ~280px wide, and a 48px floor forced
         the title onto a third line and pushed the CTA out again. At the 390px
         design width every value here still computes above its old floor, so
         phone rendering is unchanged. */
      .fw-title { font-size: clamp(30px, 12.5cqw, 62px); }
      .fw-subtitle { font-size: clamp(16px, 6.5cqw, 33px); }
      /* Multipliers are set so both values hit their MAX at the 390px design
         width (16% and 7.5% of 390 both overshoot), which keeps phone rendering
         bit-for-bit identical to the fixed 60px/28px they replaced. They only
         start scaling below that on a narrower frame — i.e. the small desktop
         mockup, never a phone. */
      .fw-cta {
        min-height: clamp(40px, 16cqw, 60px);
        font-size: clamp(18px, 7.5cqw, 28px);
      }
    }

    @media (max-width: 360px) {
      .fw-panel { padding-left: 9%; padding-right: 9%; }
      .fw-cta { padding-left: 60px; padding-right: 60px; }
    }

    @media (prefers-reduced-motion: reduce) {
      .fw-panel * { animation: none !important; }
      .fw-cta { transition: none; }
    }
  `}</style>
);

export default FunnelWelcomePage;
